import { BASE_URL_BACKEND } from '@/constants';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { supabase } from './supabase';
import PATH_PAGE from '@/router/path';
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken
} from './cookies';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Token refresh lock mechanism to prevent race conditions
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string | null) => void): void {
  refreshSubscribers.push(cb);
}

/**
 * Decode JWT payload without verifying signature
 * Used to check token expiration
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Check if token is expired or will expire within bufferSeconds
 */
function isTokenExpiredOrExpiringSoon(token: string, bufferSeconds = 60): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true; // If can't decode, treat as expired
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + bufferSeconds;
}

function onTokenRefreshed(token: string | null): void {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

/**
 * Attempts to refresh the auth token
 * Returns the new token or null if refresh failed
 */
async function refreshAuthToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();

  // Try to refresh with the refresh token from cookies
  if (refreshToken) {
    try {
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: refreshToken,
      });

      if (!error && data?.session) {
        setAuthToken(data.session.access_token);
        if (data.session.refresh_token) {
          setRefreshToken(data.session.refresh_token);
        }
        return data.session.access_token;
      }
    } catch (e) {
      console.error('Failed to refresh with cookie refresh token:', e);
    }
  }

  // Fallback: try standard Supabase session refresh
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data?.session) {
      setAuthToken(data.session.access_token);
      if (data.session.refresh_token) {
        setRefreshToken(data.session.refresh_token);
      }
      return data.session.access_token;
    }
  } catch (e) {
    console.error('Failed to refresh session:', e);
  }

  return null;
}

const axiosInstance = axios.create({
  baseURL: BASE_URL_BACKEND,
});

axiosInstance.interceptors.request.use(
  async function (config) {
    // First try to get token from cookies (universal login)
    let cookieToken = getAuthToken();

    // Proactively refresh if token is expired or expiring soon
    if (cookieToken && isTokenExpiredOrExpiringSoon(cookieToken)) {
      console.log('Token expired or expiring soon, refreshing proactively...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        cookieToken = newToken;
      } else {
        // If refresh failed, clear the expired token
        cookieToken = null;
      }
    }

    if (cookieToken) {
      config.headers.Authorization = `Bearer ${cookieToken}`;
      return config;
    }

    // Fallback to Supabase session if cookie token not available
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      // Save tokens to cookies for universal login
      setAuthToken(session.access_token);
      if (session.refresh_token) {
        setRefreshToken(session.refresh_token);
      }
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    return config;
  },
  function (error) {
    console.error('RequestInterceptor::', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // Only handle 401 Unauthorized errors
    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If already refreshing, queue this request to wait for the new token
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token) => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    // Start refresh process
    isRefreshing = true;

    try {
      const newToken = await refreshAuthToken();

      if (newToken) {
        // Notify all queued requests
        onTokenRefreshed(newToken);

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }

      // Refresh failed - notify queued requests and redirect to login
      onTokenRefreshed(null);
      removeAuthToken();
      removeRefreshToken();
      await supabase.auth.signOut();
      window.location.href = PATH_PAGE.auth.signIn;
      return Promise.reject(error);
    } catch (refreshError) {
      console.error('Failed to refresh token:', refreshError);
      onTokenRefreshed(null);
      removeAuthToken();
      removeRefreshToken();
      await supabase.auth.signOut();
      window.location.href = PATH_PAGE.auth.signIn;
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
